/**
 * Explore / visualize statistics for one attribute column.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export interface FrequencyRow {
  readonly value: string;
  readonly count: number;
}

export interface ColumnExploreStats {
  readonly kind: 'numeric' | 'categorical';
  readonly column: string;
  readonly count: number;
  readonly nulls: number;
  readonly uniqueCount: number;
  readonly min?: number;
  readonly max?: number;
  readonly mean?: number;
  readonly median?: number;
  readonly stddev?: number;
  readonly mostFrequent?: FrequencyRow;
  readonly frequencies: ReadonlyArray<FrequencyRow>;
}

export interface ChartBar {
  readonly label: string;
  readonly count: number;
}

export interface ColumnChartSeries {
  readonly title: string;
  readonly xLabel: string;
  readonly yLabel: string;
  readonly bars: ReadonlyArray<ChartBar>;
}

const DEFAULT_NUMERIC_BINS = 12;
const HIGH_CARDINALITY = 40;

export function exploreColumnStats(
  rows: ReadonlyArray<VectorExportFeature>,
  column: string,
): ColumnExploreStats {
  const values = rows.map((row) => row.properties?.[column]);
  const present = values.filter((value) => !isEmpty(value));
  const numbers = present
    .map(asFiniteNumber)
    .filter((value): value is number => value !== null);
  const frequencies = countFrequencies(present.map(stringifyValue));
  const mostFrequent = frequencies[0];

  if (numbers.length === present.length && present.length > 0) {
    const sorted = [...numbers].sort((a, b) => a - b);
    const mean = numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
    return {
      kind: 'numeric',
      column,
      count: present.length,
      nulls: values.length - present.length,
      uniqueCount: new Set(numbers).size,
      min: sorted[0],
      max: sorted[sorted.length - 1],
      mean,
      median: medianOf(sorted),
      stddev: stddevOf(numbers, mean),
      mostFrequent,
      frequencies: frequencies.slice(0, 12),
    };
  }

  return {
    kind: 'categorical',
    column,
    count: present.length,
    nulls: values.length - present.length,
    uniqueCount: frequencies.length,
    mostFrequent,
    frequencies: frequencies.slice(0, 20),
  };
}

export function visualizeColumnCounts(
  rows: ReadonlyArray<VectorExportFeature>,
  column: string,
  options?: { readonly maxBars?: number; readonly numericBins?: number },
): ColumnChartSeries {
  const values = rows.map((row) => row.properties?.[column]).filter((value) => !isEmpty(value));
  const numbers = values
    .map(asFiniteNumber)
    .filter((value): value is number => value !== null);
  const numericBins = options?.numericBins ?? DEFAULT_NUMERIC_BINS;
  const maxBars = options?.maxBars ?? HIGH_CARDINALITY;

  if (numbers.length === values.length && values.length > 0 && uniqueCount(numbers) > maxBars) {
    return {
      title: `Counts by ${column}`,
      xLabel: column,
      yLabel: 'Count',
      bars: binNumbers(numbers, numericBins),
    };
  }

  const frequencies = countFrequencies(values.map(stringifyValue)).slice(0, maxBars);
  return {
    title: `Counts by ${column}`,
    xLabel: column,
    yLabel: 'Count',
    bars: frequencies.map((row) => ({ label: row.value, count: row.count })),
  };
}

function countFrequencies(values: ReadonlyArray<string>): FrequencyRow[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
}

function binNumbers(values: ReadonlyArray<number>, binCount: number): ChartBar[] {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return [{ label: formatNumber(min), count: values.length }];
  }
  const width = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, () => 0);
  for (const value of values) {
    const index = Math.min(binCount - 1, Math.floor((value - min) / width));
    bins[index] += 1;
  }
  return bins.map((count, index) => {
    const start = min + index * width;
    const end = index === binCount - 1 ? max : start + width;
    return { label: `${formatNumber(start)}–${formatNumber(end)}`, count };
  });
}

function medianOf(sorted: ReadonlyArray<number>): number {
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

function stddevOf(values: ReadonlyArray<number>, mean: number): number {
  if (values.length < 2) return 0;
  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function uniqueCount(values: ReadonlyArray<number>): number {
  return new Set(values).size;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toPrecision(4).replace(/\.?0+$/, '');
}
